import { Button, Center, Flex, type FlexProps, Heading, Icon, Text } from "@chakra-ui/react";
import {
  ChainFullIds,
  getDefaultDerivationPathByFullChainId,
  useChainRegistry,
} from "@umami/chains";
import { type SocialAccount } from "@umami/core";
import {
  accountsActions,
  clearStorageWithReload,
  useAppDispatch,
  useAppSelector,
  useAsyncActionHandler,
  useLoginToWallet,
  useSetupSaplingAccountsForExistingWallets,
} from "@umami/state";
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { LoginButton } from "./LoginButton";
import { LogoLightIcon, TezosLogoIcon } from "../../assets/icons";
import { PasswordInput } from "../../components/PasswordInput";
import { usePasskey } from "../../hooks/usePasskey";
import { useColor } from "../../styles/useColor";
import { getDefaultAccount, getInitialAccounts } from "../../utils/accounts";
import { setupPersistence, store } from "../../utils/store";

export const SessionLogin = () => {
  const color = useColor();
  const dispatch = useAppDispatch();
  const { registry } = useChainRegistry();
  const { configs } = useAppSelector(s => s.networks);

  const {
    authenticate,
    isPasskeyFlow,
    isPasskeyUser,
    getIsPasskeyAccount,
    isLoading: isPasskeyLoading,
  } = usePasskey();

  const [defaultAccount] = useState(getDefaultAccount);
  const setupSaplingAccountsForExistingWallets = useSetupSaplingAccountsForExistingWallets();
  const passwordRef = useRef<string | undefined>(undefined);

  const { isLoading, handleLogin } = useLoginToWallet(defaultAccount, async result => {
    await setupPersistence(result, async () => {
      // Add here Solana or any new implemented chains to enable it automatically for existing users once they login.
      const newChains = [ChainFullIds.BITCOIN];

      for (const chainFullId of newChains) {
        const isChainAlreadyEnabled = defaultAccount.autoEnabledChains?.includes(chainFullId);

        // Skip if this chain has already been auto-enabled for this account
        if (isChainAlreadyEnabled) {
          continue;
        }
        let createWalletOptions;

        if (defaultAccount.type === "mnemonic") {
          const derivationPath =
            defaultAccount.derivationPaths?.[chainFullId] ||
            getDefaultDerivationPathByFullChainId(chainFullId);
          // For mnemonic accounts, result is the decrypted mnemonic phrase
          createWalletOptions = {
            mnemonic: result,
            derivationPath,
            derivationPaths: {
              [chainFullId]: derivationPath,
            },
            curve: defaultAccount.curve,
          };
        } else {
          // For secret_key and social accounts, result is the secret key
          createWalletOptions = {
            secretKey: result,
          };
        }
        const chainData = await registry.addChain(
          chainFullId,
          configs[chainFullId],
          createWalletOptions
        );

        dispatch(
          accountsActions.addAccountChain({
            accountId: defaultAccount.id ?? "",
            chainId: chainFullId,
            chainData,
          })
        );

        // Add this chain to the autoEnabledChains array to prevent re-enabling
        dispatch(
          accountsActions.markChainAsAutoEnabled({
            accountId: defaultAccount.id ?? "",
            chainId: chainFullId,
          })
        );
      }

      if (defaultAccount?.type !== "ledger") {
        const getState = () => store.getState();

        if (defaultAccount.type === "social") {
          await setupSaplingAccountsForExistingWallets({
            getState,
            accountType: "social",
            secretKey: result,
            accountPkh: defaultAccount.address.pkh,
          });
        } else {
          await setupSaplingAccountsForExistingWallets({
            getState,
            accountType: defaultAccount.type as "mnemonic" | "secret_key",
            password: passwordRef.current,
          });
        }
      }
    });
  });
  const { handleAsyncAction } = useAsyncActionHandler();

  const form = useForm({
    defaultValues: {
      password: "",
    },
    mode: "onBlur",
  });
  const isPasskeyAccount = isPasskeyFlow && getIsPasskeyAccount(defaultAccount);
  const isUnlockDisabled = !(isPasskeyAccount && isPasskeyUser) && !form.formState.isValid;

  const handleSubmit = () =>
    handleAsyncAction(async () => {
      if (!defaultAccount) {
        return clearStorageWithReload();
      }

      if (defaultAccount.type === "social") {
        await handleLogin<SocialAccount>()();
      } else if (isPasskeyFlow && isPasskeyUser) {
        const { dek } = await authenticate();
        passwordRef.current = dek;
        await handleLogin()(getInitialAccounts(), { password: dek });
      } else {
        const password = form.getValues().password;
        passwordRef.current = password;
        await handleLogin()(getInitialAccounts(), { password });
      }
    });

  return (
    <FormProvider {...form}>
      <Flex alignItems={{ base: "end", md: "center" }} justifyContent="center" height="100vh">
        <Flex
          alignItems="center"
          alignContent="start"
          flexDirection="column"
          gap="36px"
          width={{ base: "full", md: "510px" }}
          padding={{ base: "36px", md: "36px 42px" }}
          color={color("700")}
          fontSize="14px"
          border="1px solid"
          borderColor={color("100")}
          borderTopRadius="30px"
          borderBottomRadius={{ base: 0, md: "30px" }}
          backgroundColor={color("white")}
        >
          <Flex alignItems="center" justifyContent="end" flexDirection="column" gridArea="header">
            <Icon as={LogoLightIcon} width="42px" height="42px" />
            <Heading marginTop="18px" color={color("900")} size={{ base: "2xl", md: "3xl" }}>
              Welcome back!
            </Heading>
            <Text marginTop="6px" color={color("600")} size={{ base: "md", md: "lg" }}>
              You need to sign back in to use your wallet.
            </Text>
          </Flex>
          {defaultAccount?.type === "social" ? (
            <LoginButton
              idp={defaultAccount.idp}
              isLoading={isLoading}
              onSubmit={handleSubmit}
              prefix="Sign in with"
            />
          ) : (
            <>
              {!(isPasskeyAccount && isPasskeyUser) && <PasswordInput inputName="password" />}
              <Button
                width="full"
                isDisabled={isUnlockDisabled}
                isLoading={isLoading || isPasskeyLoading}
                onClick={handleSubmit}
                size="lg"
                variant="primary"
              >
                Unlock
              </Button>
            </>
          )}

          <Logo alignSelf="end" gridArea="tezos-logo" />
        </Flex>
      </Flex>
    </FormProvider>
  );
};

const Logo = (props: FlexProps) => (
  <Center gap="10px" width="full" {...props}>
    <Text color="gray.400" size="sm">
      Powered by
    </Text>
    <Icon as={TezosLogoIcon} width="auto" height={{ base: "24px", md: "36px" }} color="gray.400" />
  </Center>
);
